// Libs
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cx from 'classnames';
import { useTheme } from '@material-ui/core/styles';

// Components
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import LinearProgress from '@material-ui/core/LinearProgress';
import ResultBanner from 'COMPONENTS/ResultBanner';
import BrowserDiagnostic from 'COMPONENTS/BrowserDiagnostic';
import CameraDiagnostic from 'COMPONENTS/CameraDiagnostic';
import MicrophoneDiagnostic from 'COMPONENTS/MicrophoneDiagnostic';
import SpeakersDiagnostic from 'COMPONENTS/SpeakersDiagnostic';
import ConnectivityDiagnostic from 'COMPONENTS/ConnectivityDiagnostic';
import NavBar from 'COMPONENTS/NavBar';

// Actions
import { runCurrentTestAgain } from 'REDUCERS/navSlice';

// Utils
import {
  BROWSER_CHECK,
  CAMERA_CHECK,
  CONNECTIVITY_CHECK,
  MICROPHONE_CHECK,
  SPEAKERS_CHECK,
  DONE,
  RUNNING,
  NARROW_LAYOUT,
} from 'UTILS/constants';
import useTranslator from 'UTILS/hooks/useTranslator';
import { onTrackedClick } from 'UTILS/callbacks';
import { CHECK_ICON_MAP } from 'CHECKS/index';
import translations from './locales.json';

export const DIAGNOSTIC_COMPONENT_MAP = {
  [BROWSER_CHECK]: BrowserDiagnostic,
  [CAMERA_CHECK]: CameraDiagnostic,
  [MICROPHONE_CHECK]: MicrophoneDiagnostic,
  [SPEAKERS_CHECK]: SpeakersDiagnostic,
  [CONNECTIVITY_CHECK]: ConnectivityDiagnostic,
};

export const DIAGNOSTIC_LOADER_HINT_MAP = {
  [BROWSER_CHECK]: null,
  [CAMERA_CHECK]: null,
  [MICROPHONE_CHECK]: 'makeASoundToDetectTheMicrophone',
  [SPEAKERS_CHECK]: null,
  [CONNECTIVITY_CHECK]: null,
};

const DiagnosticCard = ({ checkLabel }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isSmallScreen = useSelector(state => state.app.layout === NARROW_LAYOUT);
  const { resultList, stage, progress, isPrompting } = useSelector(state => state.checks[checkLabel]);
  const [translate] = useTranslator(translations);

  const Diagnostic = DIAGNOSTIC_COMPONENT_MAP[checkLabel];
  const Icon = CHECK_ICON_MAP[checkLabel];

  const containerClasses = cx('diagnosticCard-container', {
    'diagnosticCard-container_largeScreen': !isSmallScreen,
  });

  const handleRunAgain = () => {
    onTrackedClick(`run_check_again`, checkLabel);
    dispatch(runCurrentTestAgain());
  };

  return (
    <Box
      className={containerClasses}
    >
      <Box
        bgcolor={theme.palette.background.paper}
        className='diagnosticCard-surface'
      >
        <Box className='diagnosticCard-header'>
          <Box className='diagnosticCard-title'>
            <Icon className='diagnosticCard-titleIcon' htmlColor={theme.palette.grey[700]} />
            <Typography className='diagnosticCard-titleText' variant='subtitle2'>
              {translate(`${checkLabel}Check`)}
            </Typography>
          </Box>
          {!isSmallScreen && stage === DONE && (
            <Box className='diagnosticCard-again'>
              <Button variant='outlined' color='primary' onClick={handleRunAgain}>{translate('runTestAgain')}</Button>
            </Box>
          )}
        </Box>
        {isSmallScreen && resultList.length > 0 && (
          <ResultBanner resultList={resultList} checkLabel={checkLabel} />
        )}
        {isSmallScreen && stage === DONE && (
          <Box className='diagnosticCard-again'>
            <Button variant='outlined' color='primary' onClick={handleRunAgain}>{translate('runTestAgain')}</Button>
          </Box>
        )}
        {stage === RUNNING && !isPrompting ? (
          <Box className='diagnosticCard-loader'>
            <Typography>
              {translate('finishingCheck')}
            </Typography>
            <LinearProgress className='diagnosticCard-loaderProgress' variant={progress == null ? 'indeterminate' : 'determinate'} value={progress} />
            {DIAGNOSTIC_LOADER_HINT_MAP[checkLabel] && (
              <Typography>
                {translate(DIAGNOSTIC_LOADER_HINT_MAP[checkLabel])}
              </Typography>
            )}
          </Box>
        ) : (
          <Box className='diagnosticCard-diagnostic'>
            <Diagnostic />
          </Box>
        )}
        {!isSmallScreen && resultList.length > 0 && (
          <ResultBanner resultList={resultList} checkLabel={checkLabel} />
        )}
        <NavBar />
      </Box>
    </Box>
  );
};

export default DiagnosticCard;
